import apis from '../utils/apis';

export interface MenuItem {
  id: number;
  label: string;
  path: string | null;
  icon_key?: string;
  sort_order: number;
  add_button?: boolean;
  is_parent?: boolean;
  children?: MenuItem[];
  allowed_roles?: string;
  allowed_org_types?: string;
  divider_after?: boolean;
}

export const isAdminOnlyItem = (item: MenuItem): boolean => {
  return item.allowed_roles === 'admin_only';
};

export interface MenuStructureResponse {
  success: boolean;
  role?: string;
  /**
   * @deprecated The user field in menu response is read-only for reference only.
   * DO NOT dispatch STORE_USER_INFO with this data as it's incomplete.
   * Always rely on the /auth/profile endpoint for complete user information including is_org_admin.
   */
  user?: {
    id: string | number;
    role: string;
  };
  menu_structure?: MenuItem[];
  error?: string;
}

class MenuService {
  /**
   * Fetch the sidebar menu structure from the backend.
   *
   * IMPORTANT: This method returns menu structure only. It should NEVER be used to update
   * user information in Redux. The response may contain a minimal `user` field for reference,
   * but this is NOT a complete user profile and should not be dispatched to STORE_USER_INFO.
   *
   * Always rely on the /auth/profile endpoint (via useAuth0Profile hook) for complete
   * and authoritative user data including is_org_admin, email, name, etc.
   *
   * @returns MenuStructureResponse with menu_structure array and optional minimal user reference
   */
  static async getSidebarMenu(): Promise<MenuStructureResponse> {
    try {
      const response = await apis.get<MenuStructureResponse>('/menu/sidebar');
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  static async getAllMenuItems(): Promise<MenuStructureResponse> {
    try {
      const response = await apis.get<MenuStructureResponse>('/menu/all-items');
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Extract all accessible route paths from menu structure
   * Includes both parent-level routes and nested routes (e.g., /matters, /matters/:id)
   */
  static extractAccessibleRoutes(menuStructure: MenuItem[]): string[] {
    const routes: string[] = [];

    const processMenuItems = (items: MenuItem[]) => {
      items.forEach((item) => {
        // Add the item's path if it exists and is not a placeholder
        if (item.path && item.path !== '#' && item.path !== null) {
          routes.push(item.path);
        }

        // Recursively process children
        if (item.children && item.children.length > 0) {
          processMenuItems(item.children);
        }
      });
    };

    processMenuItems(menuStructure);
    return routes;
  }

  /**
   * Check if user has access to a given route
   * Supports exact matches and prefix-based matching for nested routes
   * Examples:
   * - /matters (exact match)
   * - /matters/123 (matches /matters prefix)
   * - /people/user-id (matches /people prefix)
   */
  static hasAccessToRoute(requestedPath: string, accessibleRoutes: string[]): boolean {
    if (!requestedPath || !accessibleRoutes || accessibleRoutes.length === 0) {
      return false;
    }

    // Remove trailing slash for comparison
    const normalizedPath = requestedPath.replace(/\/$/, '');

    // Check for exact match
    if (accessibleRoutes.includes(normalizedPath)) {
      return true;
    }

    // Check for prefix match (for nested routes like /matters/:id)
    // Extract the base path (e.g., /matters from /matters/123)
    const pathSegments = normalizedPath.split('/').filter(Boolean);

    for (let i = pathSegments.length; i > 0; i--) {
      const basePath = '/' + pathSegments.slice(0, i).join('/');
      if (accessibleRoutes.includes(basePath)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a route requires admin-only access
   * Returns true if any menu item matching the route has admin_only in allowed_roles
   */
  static isRouteAdminOnly(requestedPath: string, menuStructure: MenuItem[]): boolean {
    if (!requestedPath || !menuStructure || menuStructure.length === 0) {
      return false;
    }

    const normalizedPath = requestedPath.replace(/\/$/, '');

    const checkMenuItems = (items: MenuItem[]): boolean => {
      for (const item of items) {
        if (item.path && item.path !== '#' && item.path !== null) {
          // Check exact match
          if (item.path === normalizedPath) {
            return isAdminOnlyItem(item);
          }

          // Check prefix match for nested routes
          if (normalizedPath.startsWith(item.path + '/') || (item.path === '/' && normalizedPath.startsWith('/'))) {
            if (isAdminOnlyItem(item)) {
              return true;
            }
          }
        }

        // Recursively check children
        if (item.children && item.children.length > 0) {
          if (checkMenuItems(item.children)) {
            return true;
          }
        }
      }

      return false;
    };

    return checkMenuItems(menuStructure);
  }
}

export default MenuService;
